if (!customElements.get('personalisation-drawer')) {
  customElements.define(
    'personalisation-drawer',
    class PersonalisationDrawer extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('#PersonalisationDrawer-Overlay');
        this.inner = this.querySelector('.personalisation-drawer__inner');
        this.textInput = this.querySelector('#PersonalisationText');

        if (this.textInput) {
          this.textInput.contentEditable = 'true';
          this.textInput.setAttribute('spellcheck', 'false');
          this.textInput.setAttribute('autocorrect', 'off');
        }

        this.counter = this.querySelector('#PersonalisationCounter');
        this.colourNameEl = this.querySelector('#PersonalisationColourName');

        this.selectedColour = null;
        this.selectedColourName = null;
        this.selectedIcon = null;
        this.maxLen = PersonalisationUtils.normaliseCharacterLimit(this.dataset.characterLimit);
        this.previewX = this._normaliseOffsetCoordinate(this.dataset.previewX, 0);
        this.previewY = this._normaliseOffsetCoordinate(this.dataset.previewY, 0);
        this.previewRotation = this._normaliseRotation(this.dataset.previewRotation, 0);
        this.initialPreviewX = this.previewX;
        this.initialPreviewY = this.previewY;
        this.initialPreviewRotation = this.previewRotation;
        this.calibratorActive = this._isCalibratorActive();
        this.calibratorInputs = {};
        this.calibratorQueue = this._getCalibratorQueue();
        this.calibratorQueueControls = {};

        this._applyStaticState();
        this._normaliseSymbolDisplay();
        this._bindEvents();
        this._bindPreviewPositioning();
        this._initCalibrator();
      }

      _applyStaticState() {
        const chooser = this.querySelector('.personalisation-drawer__mode');
        const symbolSection = this.querySelector('.personalisation-drawer__symbols');
        const colourSection = this.querySelector('.personalisation-drawer__colours');

        this.dataset.activeMode = 'mixed';

        if (chooser) chooser.hidden = true;
        if (symbolSection) symbolSection.hidden = false;
        if (colourSection) colourSection.hidden = false;
        if (this.colourNameEl) this.colourNameEl.hidden = true;

        if (this.textInput) {
          this.textInput.dataset.symbolOnly = 'false';
          this.textInput.dataset.placeholder = 'Text';
          this.textInput.setAttribute(
            'aria-label',
            `Enter personalisation text, ${this.maxLen} characters maximum`
          );
        }

        this._updateCounter();
      }

      _normaliseOffsetCoordinate(value, fallback) {
        const offset = Number.parseFloat(value);
        const safeOffset = Number.isFinite(offset) ? offset : fallback;
        return Math.min(Math.max(50 + safeOffset, 0), 100) / 100;
      }

      _normaliseRotation(value, fallback) {
        const rotation = Number.parseFloat(value);
        return Number.isFinite(rotation) ? rotation : fallback;
      }

      _isCalibratorActive() {
        if (this.dataset.calibratorEnabled !== 'true') return false;

        const params = new URLSearchParams(window.location.search);
        return (
          params.get('personalisation_calibrator') === '1' ||
          params.get('personalization_calibrator') === '1'
        );
      }

      _getCalibratorQueue() {
        const queueEl = this.querySelector('[data-calibrator-products]');
        if (!queueEl) return { products: [] };

        try {
          const data = JSON.parse(queueEl.textContent);
          const products = Array.isArray(data.products)
            ? data.products.filter((item) => item && item.handle && item.url)
            : [];

          return {
            collectionTitle: data.collectionTitle || '',
            collectionUrl: data.collectionUrl || '',
            totalProducts: Number(data.totalProducts) || products.length,
            products,
          };
        } catch (error) {
          console.warn('Personalisation calibrator products could not be parsed.', error);
          return { products: [] };
        }
      }

      _normaliseSymbolDisplay() {
        this.querySelectorAll('.personalisation-drawer__symbol-btn').forEach((btn) => {
          if (btn.querySelector('img')) return;
          const char = btn.textContent.trim();
          if (char) btn.textContent = char;
        });
      }

      _bindEvents() {
        if (this.overlay) this.overlay.addEventListener('click', () => this.close());

        document.addEventListener('keyup', (e) => {
          if (e.code === 'Escape' && this.classList.contains('active')) this.close();
        });

        this.querySelector('.personalisation-drawer__close').addEventListener('click', () => this.close());
        this.querySelector('#PersonalisationDone').addEventListener('click', () => this.done());
        this.querySelector('#PersonalisationClear').addEventListener('click', () => this.clear());

        this.querySelectorAll('.personalisation-drawer__colour-dot').forEach((dot) => {
          dot.addEventListener('click', () => this._selectColour(dot));
        });

        this.querySelectorAll('.personalisation-drawer__symbol-btn').forEach((btn) => {
          btn.addEventListener('click', () => this._selectSymbol(btn));
          btn.addEventListener('mousedown', (e) => e.preventDefault());
        });

        if (this.textInput) {
          this.textInput.addEventListener('input', () => {
            this._trimContentToMax();
            this._updateCounter();
            this._updatePreview();
          });

          this.textInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              return;
            }

            if (
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key.length !== 1 ||
              e.ctrlKey ||
              e.metaKey
            ) {
              return;
            } else if (/^[a-zA-Z0-9]$/.test(e.key) === false) {
              e.preventDefault();
              return;
            }

            if (this._getContentCount() >= this.maxLen && !this._hasSelectionInInput()) {
              e.preventDefault();
            }
          });

          this.textInput.addEventListener('paste', (e) => {
            e.preventDefault();

            if (this._hasSelectionInInput()) {
              this._deleteSelectionInInput();
            }

            const text = (e.clipboardData || window.clipboardData).getData('text');
            const available = this.maxLen - this._getContentCount();
            if (available <= 0) return;

            document.execCommand('insertText', false, [...text].slice(0, available).join(''));
          });
        }
      }

      _bindPreviewPositioning() {
        const panel = this.querySelector('.personalisation-drawer__image-panel');
        if (!panel) return;

        this._handlePreviewResize = () => this._updatePreviewPosition();

        if ('ResizeObserver' in window) {
          this.previewResizeObserver = new ResizeObserver(this._handlePreviewResize);
          this.previewResizeObserver.observe(panel);
        }

        window.addEventListener('resize', this._handlePreviewResize);
      }

      _initCalibrator() {
        if (!this.calibratorActive) return;

        this.classList.add('personalisation-drawer--calibrator');
        this._renderCalibratorPanel();
        this._bindCalibratorDrag();
        this._syncCalibratorControls();

        window.setTimeout(() => this.open());
      }

      _renderCalibratorPanel() {
        const formPanel = this.querySelector('.personalisation-drawer__form-panel');
        const actions = this.querySelector('.personalisation-drawer__actions');
        if (!formPanel || this.querySelector('.personalisation-calibrator')) return;

        const panel = document.createElement('div');
        panel.className = 'personalisation-calibrator';
        panel.innerHTML = `
          <div class="personalisation-calibrator__header">
            <h3 class="personalisation-calibrator__title">Position calibrator</h3>
            <p class="personalisation-calibrator__copy">Drag the preview on the product image, then copy each value into the matching product metafield.</p>
          </div>

          <div class="personalisation-calibrator__queue" data-calibrator-queue hidden>
            <div class="personalisation-calibrator__queue-header">
              <span data-calibrator-queue-status></span>
              <label class="personalisation-calibrator__toggle">
                <input type="checkbox" data-calibrator-missing-only>
                <span>Missing positions only</span>
              </label>
            </div>
            <div class="personalisation-calibrator__nav">
              <a href="#" data-calibrator-nav="prev">Previous</a>
              <select data-calibrator-jump aria-label="Jump to product"></select>
              <a href="#" data-calibrator-nav="next">Next</a>
            </div>
            <p class="personalisation-calibrator__queue-note" data-calibrator-queue-note></p>
          </div>

          <div class="personalisation-calibrator__grid">
            <label class="personalisation-calibrator__field">
              <span>Personalisation X position</span>
              <span class="personalisation-calibrator__control">
                <input type="number" step="0.1" min="-50" max="50" data-calibrator-input="x">
                <button type="button" data-calibrator-copy="x">Copy</button>
              </span>
            </label>

            <label class="personalisation-calibrator__field">
              <span>Personalisation Y position</span>
              <span class="personalisation-calibrator__control">
                <input type="number" step="0.1" min="-50" max="50" data-calibrator-input="y">
                <button type="button" data-calibrator-copy="y">Copy</button>
              </span>
            </label>

            <label class="personalisation-calibrator__field">
              <span>Personalisation rotation</span>
              <span class="personalisation-calibrator__control">
                <input type="number" step="0.1" data-calibrator-input="rotation">
                <button type="button" data-calibrator-copy="rotation">Copy</button>
              </span>
            </label>
          </div>

          <label class="personalisation-calibrator__range">
            <span>Rotate</span>
            <input type="range" min="-45" max="45" step="0.5" data-calibrator-input="rotationRange">
          </label>

          <div class="personalisation-calibrator__footer">
            <a href="#" target="_blank" rel="noopener" data-calibrator-admin-link>Edit product in Admin</a>
            <button type="button" data-calibrator-reset>Reset</button>
          </div>
        `;

        if (actions) {
          actions.before(panel);
        } else {
          formPanel.appendChild(panel);
        }

        this.calibratorInputs = {
          x: panel.querySelector('[data-calibrator-input="x"]'),
          y: panel.querySelector('[data-calibrator-input="y"]'),
          rotation: panel.querySelector('[data-calibrator-input="rotation"]'),
          rotationRange: panel.querySelector('[data-calibrator-input="rotationRange"]'),
        };

        this.calibratorQueueControls = {
          wrapper: panel.querySelector('[data-calibrator-queue]'),
          status: panel.querySelector('[data-calibrator-queue-status]'),
          note: panel.querySelector('[data-calibrator-queue-note]'),
          missingOnly: panel.querySelector('[data-calibrator-missing-only]'),
          jump: panel.querySelector('[data-calibrator-jump]'),
          prev: panel.querySelector('[data-calibrator-nav="prev"]'),
          next: panel.querySelector('[data-calibrator-nav="next"]'),
        };

        this._bindCalibratorQueue();
        this._syncCalibratorQueue();

        if (this.calibratorInputs.x) {
          this.calibratorInputs.x.addEventListener('input', () =>
            this._setCalibratorCoordinate('x', this.calibratorInputs.x.value, 'x')
          );
        }

        if (this.calibratorInputs.y) {
          this.calibratorInputs.y.addEventListener('input', () =>
            this._setCalibratorCoordinate('y', this.calibratorInputs.y.value, 'y')
          );
        }

        if (this.calibratorInputs.rotation) {
          this.calibratorInputs.rotation.addEventListener('input', () =>
            this._setCalibratorRotation(this.calibratorInputs.rotation.value, 'rotation')
          );
        }

        if (this.calibratorInputs.rotationRange) {
          this.calibratorInputs.rotationRange.addEventListener('input', () =>
            this._setCalibratorRotation(this.calibratorInputs.rotationRange.value, 'rotationRange')
          );
        }

        panel.querySelectorAll('[data-calibrator-copy]').forEach((button) => {
          button.addEventListener('click', () => this._copyCalibratorValue(button));
        });

        const adminLink = panel.querySelector('[data-calibrator-admin-link]');
        if (adminLink && this.dataset.productAdminUrl) {
          adminLink.href = this.dataset.productAdminUrl;
        } else if (adminLink) {
          adminLink.hidden = true;
        }

        const reset = panel.querySelector('[data-calibrator-reset]');
        if (reset) reset.addEventListener('click', () => this._resetCalibratorValues());
      }

      _bindCalibratorQueue() {
        const controls = this.calibratorQueueControls;
        if (!controls || !controls.wrapper) return;

        if (controls.missingOnly) {
          controls.missingOnly.addEventListener('change', () => this._syncCalibratorQueue());
        }

        if (controls.jump) {
          controls.jump.addEventListener('change', () => {
            if (controls.jump.value) window.location.href = controls.jump.value;
          });
        }
      }

      _syncCalibratorQueue() {
        const controls = this.calibratorQueueControls;
        const products = this.calibratorQueue.products || [];
        if (!controls || !controls.wrapper || !products.length) return;

        const filteredProducts = this._getFilteredCalibratorProducts();
        const currentHandle = this.dataset.productHandle;
        const currentIndex = filteredProducts.findIndex((item) => item.handle === currentHandle);
        const currentProduct = products.find((item) => item.handle === currentHandle);
        const missingProducts = products.filter((item) => item.missingPosition);
        const missingOnly = !!controls.missingOnly?.checked;

        controls.wrapper.hidden = false;

        if (controls.status) {
          const currentPosition = currentIndex >= 0 ? currentIndex + 1 : null;
          const totalProducts = this.calibratorQueue.totalProducts || products.length;
          const visibleCount = filteredProducts.length;
          const collectionName = this.calibratorQueue.collectionTitle || 'calibration collection';

          if (currentPosition) {
            controls.status.textContent = `${currentPosition} of ${visibleCount} in ${collectionName}`;
          } else if (missingOnly && visibleCount) {
            controls.status.textContent = `${visibleCount} of ${totalProducts} products missing positions`;
          } else {
            controls.status.textContent = `${products.length} products in ${collectionName}`;
          }
        }

        if (controls.note) {
          const noteParts = [];

          if (currentProduct?.missingPosition && currentProduct.missingFields?.length) {
            noteParts.push(`Current product missing: ${currentProduct.missingFields.join(', ')}`);
          } else if (currentProduct) {
            noteParts.push('Current product has position values.');
          }

          if (this.calibratorQueue.totalProducts > products.length) {
            noteParts.push(`Showing first ${products.length} products from the collection.`);
          }

          if (missingOnly && !filteredProducts.length) {
            noteParts.push('No products with missing positions in this queue.');
          } else if (!missingOnly && missingProducts.length) {
            noteParts.push(`${missingProducts.length} missing positions.`);
          }

          controls.note.textContent = noteParts.join(' ');
          controls.note.hidden = !controls.note.textContent;
        }

        this._renderCalibratorJumpOptions(filteredProducts, currentHandle);
        this._setCalibratorNavLink(controls.prev, filteredProducts[currentIndex - 1]);
        this._setCalibratorNavLink(
          controls.next,
          currentIndex >= 0 ? filteredProducts[currentIndex + 1] : filteredProducts[0]
        );
      }

      _getFilteredCalibratorProducts() {
        const products = this.calibratorQueue.products || [];
        if (!this.calibratorQueueControls?.missingOnly?.checked) return products;
        return products.filter((item) => item.missingPosition);
      }

      _renderCalibratorJumpOptions(products, currentHandle) {
        const select = this.calibratorQueueControls?.jump;
        if (!select) return;

        select.innerHTML = '';

        if (!products.length) {
          const option = document.createElement('option');
          option.textContent = 'No products found';
          option.value = '';
          select.appendChild(option);
          select.disabled = true;
          return;
        }

        select.disabled = false;

        products.forEach((item, index) => {
          const option = document.createElement('option');
          option.value = this._getCalibratorProductUrl(item);
          option.textContent = `${index + 1}. ${item.title}${item.missingPosition ? ' (missing)' : ''}`;
          option.selected = item.handle === currentHandle;
          select.appendChild(option);
        });

        if (![...select.options].some((option) => option.selected)) {
          select.selectedIndex = 0;
        }
      }

      _setCalibratorNavLink(link, product) {
        if (!link) return;

        if (!product) {
          link.href = '#';
          link.setAttribute('aria-disabled', 'true');
          link.tabIndex = -1;
          return;
        }

        link.href = this._getCalibratorProductUrl(product);
        link.removeAttribute('aria-disabled');
        link.tabIndex = 0;
      }

      _getCalibratorProductUrl(product) {
        const url = new URL(product.url, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);

        currentParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });

        url.searchParams.delete('personalization_calibrator');
        url.searchParams.set('personalisation_calibrator', '1');

        return url.toString();
      }

      _bindCalibratorDrag() {
        const panel = this.querySelector('.personalisation-drawer__image-panel');
        const wrap = this.querySelector('.personalisation-drawer__preview-wrap');
        if (!panel || !wrap) return;

        wrap.tabIndex = 0;
        wrap.setAttribute(
          'aria-label',
          'Personalisation preview position. Drag or use arrow keys to move.'
        );

        this._handleCalibratorPointerDown = (event) => {
          if (event.button !== 0) return;
          if (event.target.closest('button, input, a, textarea, select')) return;

          const imageRect = this._getRenderedImageRect();
          if (!imageRect) return;

          event.preventDefault();
          panel.setPointerCapture(event.pointerId);

          if (!event.target.closest('.personalisation-drawer__preview-wrap')) {
            this._setCalibratorFromPointer(event, imageRect);
          }

          this.calibratorDrag = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPreviewX: this.previewX,
            startPreviewY: this.previewY,
          };
        };

        this._handleCalibratorPointerMove = (event) => {
          if (!this.calibratorDrag || this.calibratorDrag.pointerId !== event.pointerId) return;

          const imageRect = this._getRenderedImageRect();
          if (!imageRect) return;

          const deltaX = (event.clientX - this.calibratorDrag.startClientX) / imageRect.width;
          const deltaY = (event.clientY - this.calibratorDrag.startClientY) / imageRect.height;

          this.previewX = this._clampRatio(this.calibratorDrag.startPreviewX + deltaX);
          this.previewY = this._clampRatio(this.calibratorDrag.startPreviewY + deltaY);
          this._updatePreviewPosition();
          this._syncCalibratorControls();
        };

        this._handleCalibratorPointerUp = (event) => {
          if (!this.calibratorDrag || this.calibratorDrag.pointerId !== event.pointerId) return;
          this.calibratorDrag = null;
          if (panel.hasPointerCapture(event.pointerId)) panel.releasePointerCapture(event.pointerId);
        };

        this._handleCalibratorKeydown = (event) => {
          const movementKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
          if (!movementKeys.includes(event.key)) return;

          event.preventDefault();
          const step = event.shiftKey ? 0.05 : 0.01;

          if (event.key === 'ArrowLeft') this.previewX = this._clampRatio(this.previewX - step);
          if (event.key === 'ArrowRight') this.previewX = this._clampRatio(this.previewX + step);
          if (event.key === 'ArrowUp') this.previewY = this._clampRatio(this.previewY - step);
          if (event.key === 'ArrowDown') this.previewY = this._clampRatio(this.previewY + step);

          this._updatePreviewPosition();
          this._syncCalibratorControls();
        };

        panel.addEventListener('pointerdown', this._handleCalibratorPointerDown);
        panel.addEventListener('pointermove', this._handleCalibratorPointerMove);
        panel.addEventListener('pointerup', this._handleCalibratorPointerUp);
        panel.addEventListener('pointercancel', this._handleCalibratorPointerUp);
        wrap.addEventListener('keydown', this._handleCalibratorKeydown);
      }

      disconnectedCallback() {
        if (this.previewResizeObserver) this.previewResizeObserver.disconnect();
        if (this._handlePreviewResize) window.removeEventListener('resize', this._handlePreviewResize);

        const panel = this.querySelector('.personalisation-drawer__image-panel');
        const wrap = this.querySelector('.personalisation-drawer__preview-wrap');
        if (panel && this._handleCalibratorPointerDown) {
          panel.removeEventListener('pointerdown', this._handleCalibratorPointerDown);
          panel.removeEventListener('pointermove', this._handleCalibratorPointerMove);
          panel.removeEventListener('pointerup', this._handleCalibratorPointerUp);
          panel.removeEventListener('pointercancel', this._handleCalibratorPointerUp);
        }
        if (wrap && this._handleCalibratorKeydown) {
          wrap.removeEventListener('keydown', this._handleCalibratorKeydown);
        }
      }

      open() {
        this._syncProductImage();
        this._ensureDefaultColour();
        this._updatePreviewPosition();

        document.body.classList.add('overflow-hidden');
        setTimeout(() => {
          this.classList.add('animate', 'active');
        });

        this.addEventListener(
          'transitionend',
          () => {
            if (!this.classList.contains('active') || !this.textInput) return;

            this._placeCursorAtEnd();
          },
          { once: true }
        );
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');

        this.addEventListener(
          'transitionend',
          () => {
            if (!this.classList.contains('active')) this.classList.remove('animate');
          },
          { once: true }
        );
      }

      done() {
        this._trimContentToMax();
        this._ensureDefaultColour();

        const value = this._getContentValue();
        if (!value) return;

        this._injectFormProperties(value, this.selectedColourName, this._getChargeableUnitCount());
        this._showSummary(value, this.selectedColourName);
        this.close();
      }

      clear() {
        this._clearContent();

        this.querySelectorAll('.personalisation-drawer__colour-dot').forEach((d) =>
          d.classList.remove('is-selected')
        );
        this.selectedColour = null;
        this.selectedColourName = null;
        this.selectedIcon = null;

        if (this.colourNameEl) this.colourNameEl.textContent = '';

        this._updatePreview();
        this._removeFormProperties();
        this._hideSummary();
      }

      _clearContent() {
        if (this.textInput) this.textInput.innerHTML = '';
        this.querySelectorAll('.personalisation-drawer__symbol-btn').forEach((btn) =>
          btn.classList.remove('is-selected')
        );
        this._updateCounter();
      }

      _selectColour(dot) {
        this.querySelectorAll('.personalisation-drawer__colour-dot').forEach((d) =>
          d.classList.remove('is-selected')
        );
        dot.classList.add('is-selected');
        this.selectedColour = dot.style.backgroundColor;
        this.selectedColourName = dot.dataset.colour;

        if (this.colourNameEl) this.colourNameEl.textContent = this.selectedColourName;

        this._updateSymbolColours();
        this._updatePreview();
      }

      _selectSymbol(btn) {
        const isSelected = btn.classList.contains('is-selected');
        this.querySelectorAll('.personalisation-drawer__symbol-btn').forEach((symbolButton) =>
          symbolButton.classList.remove('is-selected')
        );

        if (isSelected) {
          this.selectedIcon = null;
        } else {
          btn.classList.add('is-selected');
          this.selectedIcon = {
            name: btn.dataset.symbol,
            imageUrl: btn.dataset.imageUrl || '',
          };
        }

        this._updatePreview();
      }

      _createSymbolElement(name, imageUrl, className) {
        const symbol = document.createElement('span');
        symbol.className = className;
        symbol.dataset.symbolName = name;
        symbol.dataset.imageUrl = imageUrl || '';
        symbol.setAttribute('role', 'img');
        symbol.setAttribute('aria-label', name);
        symbol.setAttribute('contenteditable', 'false');

        if (imageUrl) {
          this._applySymbolMask(symbol, imageUrl);
        } else {
          symbol.textContent = name;
        }

        return symbol;
      }

      _applySymbolMask(element, imageUrl) {
        const colour = this.selectedColour || '#726869';
        element.style.backgroundColor = colour;
        element.style.webkitMaskImage = `url("${imageUrl}")`;
        element.style.maskImage = `url("${imageUrl}")`;
        element.style.webkitMaskRepeat = 'no-repeat';
        element.style.maskRepeat = 'no-repeat';
        element.style.webkitMaskPosition = 'center';
        element.style.maskPosition = 'center';
        element.style.webkitMaskSize = 'contain';
        element.style.maskSize = 'contain';
      }

      _updateSymbolColours() {
        this.querySelectorAll('[data-symbol-name][data-image-url]').forEach((symbol) => {
          const imageUrl = symbol.dataset.imageUrl;
          if (imageUrl) this._applySymbolMask(symbol, imageUrl);
        });
      }

      _updatePreview() {
        const wrap = this.querySelector('.personalisation-drawer__preview-wrap');
        if (!wrap || !this.textInput) return;

        wrap.innerHTML = '';
        this._updatePreviewPosition();

        if (this.selectedIcon) {
          wrap.appendChild(
            this._createSymbolElement(
              this.selectedIcon.name,
              this.selectedIcon.imageUrl,
              'personalisation-drawer__preview-symbol is-visible'
            )
          );
        }

        const text = this._getTextValue();
        if (text) {
          const span = document.createElement('span');
          span.className = 'personalisation-drawer__preview-text is-visible';
          span.textContent = text;
          span.style.color = this.selectedColour || '#726869';
          wrap.appendChild(span);
        }
      }

      _updatePreviewPosition() {
        const wrap = this.querySelector('.personalisation-drawer__preview-wrap');
        const imageRect = this._getRenderedImageRect();

        if (!wrap || !imageRect) return;

        wrap.style.left = `${imageRect.left + imageRect.width * this.previewX}px`;
        wrap.style.top = `${imageRect.top + imageRect.height * this.previewY}px`;
        wrap.style.transform = `translateX(-50%) rotate(${this.previewRotation}deg)`;
      }

      _getRenderedImageRect() {
        const panel = this.querySelector('.personalisation-drawer__image-panel');
        const image = document.getElementById('PersonalisationDrawer-Image');

        if (!panel || !image) return null;

        const panelRect = panel.getBoundingClientRect();
        if (!panelRect.width || !panelRect.height) return null;

        const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || 1;
        const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || 1;
        const panelRatio = panelRect.width / panelRect.height;
        const imageRatio = naturalWidth / naturalHeight;

        let renderedWidth = panelRect.width;
        let renderedHeight = panelRect.height;
        let renderedLeft = 0;
        const renderedTop = 0;

        if (imageRatio > panelRatio) {
          renderedHeight = panelRect.width / imageRatio;
        } else {
          renderedWidth = panelRect.height * imageRatio;
          renderedLeft = (panelRect.width - renderedWidth) / 2;
        }

        return {
          panelRect,
          left: renderedLeft,
          top: renderedTop,
          width: renderedWidth,
          height: renderedHeight,
        };
      }

      _setCalibratorFromPointer(event, imageRect) {
        this.previewX = this._clampRatio((event.clientX - imageRect.panelRect.left - imageRect.left) / imageRect.width);
        this.previewY = this._clampRatio((event.clientY - imageRect.panelRect.top - imageRect.top) / imageRect.height);
        this._updatePreviewPosition();
        this._syncCalibratorControls();
      }

      _setCalibratorCoordinate(axis, value, skipField) {
        const offset = Number.parseFloat(value);
        if (!Number.isFinite(offset)) return;

        if (axis === 'x') this.previewX = this._normaliseOffsetCoordinate(offset, this._getCalibratorOffset('x'));
        if (axis === 'y') this.previewY = this._normaliseOffsetCoordinate(offset, this._getCalibratorOffset('y'));

        this._updatePreviewPosition();
        this._syncCalibratorControls(skipField);
      }

      _setCalibratorRotation(value, skipField) {
        const rotation = Number.parseFloat(value);
        if (!Number.isFinite(rotation)) return;

        this.previewRotation = rotation;
        this._updatePreviewPosition();
        this._syncCalibratorControls(skipField);
      }

      _resetCalibratorValues() {
        this.previewX = this.initialPreviewX;
        this.previewY = this.initialPreviewY;
        this.previewRotation = this.initialPreviewRotation;
        this._updatePreviewPosition();
        this._syncCalibratorControls();
      }

      _syncCalibratorControls(skipField) {
        if (!this.calibratorActive || !this.calibratorInputs) return;

        const values = {
          x: this._formatCalibratorValue(this._getCalibratorOffset('x')),
          y: this._formatCalibratorValue(this._getCalibratorOffset('y')),
          rotation: this._formatCalibratorValue(this.previewRotation),
        };

        if (this.calibratorInputs.x && skipField !== 'x') this.calibratorInputs.x.value = values.x;
        if (this.calibratorInputs.y && skipField !== 'y') this.calibratorInputs.y.value = values.y;
        if (this.calibratorInputs.rotation && skipField !== 'rotation') {
          this.calibratorInputs.rotation.value = values.rotation;
        }
        if (this.calibratorInputs.rotationRange && skipField !== 'rotationRange') {
          this.calibratorInputs.rotationRange.value = String(
            Math.min(Math.max(this.previewRotation, -45), 45)
          );
        }
      }

      _copyCalibratorValue(button) {
        const key = button.dataset.calibratorCopy;
        const input = this.calibratorInputs[key];
        if (!input) return;

        const originalText = button.textContent;
        const showCopied = () => {
          button.textContent = 'Copied';
          window.setTimeout(() => {
            button.textContent = originalText;
          }, 1200);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(input.value).then(showCopied).catch(() => {
            input.select();
            document.execCommand('copy');
            showCopied();
          });
          return;
        }

        input.select();
        document.execCommand('copy');
        showCopied();
      }

      _getCalibratorOffset(axis) {
        const ratio = axis === 'x' ? this.previewX : this.previewY;
        return ratio * 100 - 50;
      }

      _formatCalibratorValue(value) {
        const rounded = Math.round(value * 100) / 100;
        if (Object.is(rounded, -0)) return '0';
        return String(rounded);
      }

      _clampRatio(value) {
        return Math.min(Math.max(value, 0), 1);
      }

      _getTextValue() {
        return this.textInput ? this.textInput.textContent || '' : '';
      }

      _getContentCount() {
        return [...this._getTextValue()].length;
      }

      _getChargeableUnitCount() {
        return PersonalisationUtils.countSerializedPersonalisationUnits(this._getContentValue());
      }

      _getContentValue() {
        return PersonalisationUtils.serializePersonalisation({
          text: this._getTextValue(),
          icon: this.selectedIcon,
        });
      }

      _trimContentToMax() {
        if (!this.textInput || this._getContentCount() <= this.maxLen) return;

        this.textInput.textContent = [...this._getTextValue()].slice(0, this.maxLen).join('');
        this._placeCursorAtEnd();
      }

      _updateCounter() {
        if (this.counter) this.counter.textContent = String(this._getContentCount());
      }

      _hasSelectionInInput() {
        if (!this.textInput) return false;

        const sel = window.getSelection();
        return (
          sel &&
          sel.rangeCount &&
          !sel.isCollapsed &&
          this.textInput.contains(sel.anchorNode) &&
          this.textInput.contains(sel.focusNode)
        );
      }

      _deleteSelectionInInput() {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        sel.deleteFromDocument();
      }

      _placeCursorAtEnd() {
        if (!this.textInput) return;

        this.textInput.focus();
        const range = document.createRange();
        range.selectNodeContents(this.textInput);
        range.collapse(false);

        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      _injectFormProperties(value, colourName, units) {
        this._removeFormProperties();

        const formId = this.dataset.productFormId;
        const form = formId ? document.getElementById(formId) : document.querySelector('product-form form');
        if (!form) return;

        const append = (name, val) => {
          const el = document.createElement('input');
          el.type = 'hidden';
          el.name = name;
          el.value = val;
          el.className = 'js-personalisation-property';
          form.appendChild(el);
        };

        if (value) append('properties[Text]', value);
        if (colourName) append('properties[Colour]', colourName);
        if (units > 0) append('properties[_personalisation_units]', String(units));
      }

      _removeFormProperties() {
        document.querySelectorAll('.js-personalisation-property').forEach((el) => el.remove());
      }

      _showSummary(text, colourName) {
        const addBtn = document.querySelector('.js-personalisation-trigger-add');
        const summary = document.querySelector('.js-personalisation-trigger-summary');
        const summaryKind = document.querySelector('.js-personalisation-summary-kind');
        const summaryText = document.querySelector('.js-personalisation-summary-text');
        const summaryColour = document.querySelector('.js-personalisation-summary-colour');
        const summaryColourWrap = document.querySelector('.js-personalisation-summary-colour-wrap');

        if (!addBtn || !summary) return;

        if (summaryKind) {
          const isIconOnly = this._isSymbolOnlyValue(text);
          summaryKind.textContent = isIconOnly ? 'Icon' : this.selectedIcon ? 'Icon + Text' : 'Text';
        }
        if (summaryText) this._renderPersonalisationValue(summaryText, text);

        if (summaryColour) summaryColour.textContent = colourName || '';
        if (summaryColourWrap) summaryColourWrap.style.display = colourName ? '' : 'none';

        addBtn.style.display = 'none';
        summary.classList.add('is-active');
      }

      _renderPersonalisationValue(container, value) {
        container.innerHTML = '';
        const parts = value.split(/(\[[^\]]+\])/);

        parts.forEach((part) => {
          const symbolMatch = part.match(/^\[(.+)\]$/);
          if (symbolMatch) {
            const name = symbolMatch[1];
            const btn = this.querySelector(`.personalisation-drawer__symbol-btn[data-symbol="${name}"]`);
            const url = btn ? btn.dataset.imageUrl : '';
            container.appendChild(
              this._createSymbolElement(name, url, 'personalisation-trigger__summary-symbol')
            );
          } else if (part) {
            container.appendChild(document.createTextNode(part));
          }
        });
      }

      _isSymbolOnlyValue(value) {
        return value.replace(/\[[^\]]+\]/g, '').trim() === '' && /\[[^\]]+\]/.test(value);
      }

      _hideSummary() {
        const addBtn = document.querySelector('.js-personalisation-trigger-add');
        const summary = document.querySelector('.js-personalisation-trigger-summary');

        if (addBtn) addBtn.style.display = '';
        if (summary) summary.classList.remove('is-active');
      }

      _ensureDefaultColour() {
        if (this.selectedColourName) return;
        const firstDot = this.querySelector('.personalisation-drawer__colour-dot');
        if (firstDot) this._selectColour(firstDot);
      }

      _syncProductImage() {
        const variantMedia = document.querySelector(
          '.product__media-item.is-active img, .product__main-photos .swiper-slide-active img'
        );
        const drawerImg = document.getElementById('PersonalisationDrawer-Image');
        if (variantMedia && drawerImg && variantMedia.src) {
          drawerImg.src = variantMedia.src;
          drawerImg.srcset = variantMedia.srcset || '';
        }
      }
    }
  );
}
